<template>
  <v-card flat>
    <v-card-title class="card-bg-2 headline pb-0" style="align-items: baseline; display: flex;" :class="{ 'grey--text': disabled }" :id="`step-${index} `">
      Date Range
      <v-menu v-model="showHelp" offset-y open-on-hover :open-delay="400">
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" @click="showHelp = true" class="clickable ml-2" color="primary">
            info_outline
          </v-icon>
        </template>
        <v-card>
          <v-card-text> Available date range is the first and last records of the selected analytes (or of all shown
            analytes if none selected). </v-card-text>
        </v-card>
      </v-menu>
    </v-card-title>
    <v-card-text class="card-bg-2 pb-0">
      <v-layout>
        <v-flex>
          <vc-date-picker v-if="!disabled" v-model="dates" mode="range" :columns="2" :available-dates="{ end: restrictions.end }" :popover="{ visibility: 'click' }">
            <v-layout>
              <v-flex md6 style="min-width: 100px;">
                <v-text-field v-model="prettyStartDate" label="From" hide-details solo class="rounded-left" prepend-inner-icon="date_range" readonly>
                  <template v-if="dates.start" v-slot:append>
                    <v-tooltip bottom :open-delay="500" :close-delay="0">
                      <v-btn small icon class="ma-0 mr-1" :disabled="disabled || !dates.start" @click.stop="previousStartDate()" slot="activator">
                        <v-icon>chevron_left</v-icon>
                      </v-btn>
                      <span>Previous day</span>
                    </v-tooltip>
                    <v-tooltip bottom :open-delay="500" :close-delay="0">
                      <v-btn small icon class="ma-0" :disabled="disabled || !hasNextStartDate || !dates.start" @click.stop="nextStartDate()" slot="activator">
                        <v-icon>chevron_right</v-icon>
                      </v-btn>
                      <span>Next day</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-flex>
              <v-flex md6 style="min-width: 100px;">
                <v-text-field v-model="prettyEndDate" label="To" hide-details solo class="rounded-right" readonly>
                  <template v-if="dates.end" v-slot:append>
                    <v-tooltip bottom :open-delay="500" :close-delay="0">
                      <v-btn small icon class="ma-0 mr-1" :disabled="disabled || !dates.end" @click.stop="previousEndDate()" slot="activator">
                        <v-icon>chevron_left</v-icon>
                      </v-btn>
                      <span>Previous day</span>
                    </v-tooltip>

                    <v-tooltip bottom :open-delay="500" :close-delay="0">
                      <v-btn small icon class="ma-0 mr-1" :disabled="disabled || !hasNextEndDate || !dates.end" @click.stop="nextEndDate()" slot="activator">
                        <v-icon>chevron_right</v-icon>
                      </v-btn>
                      <span>Next day</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-flex>
            </v-layout>
          </vc-date-picker>
          <template v-else>
            <v-layout>
              <v-flex md6 style="min-width: 100px;">
                <v-text-field disabled v-model="prettyStartDate" label="From" hide-details solo class="rounded-left" prepend-inner-icon="date_range" readonly>
                  <template v-if="dates.start" v-slot:append>
                    <v-btn small icon class="ma-0 mr-1" disabled>
                      <v-icon>chevron_left</v-icon>
                    </v-btn>
                    <v-btn small icon class="ma-0" disabled>
                      <v-icon>chevron_right</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-flex>
              <v-flex md6 style="min-width: 100px;">
                <v-text-field disabled v-model="prettyEndDate" label="To" hide-details solo class="rounded-right" readonly>
                  <template v-if="dates.end" v-slot:append>
                    <v-btn small icon class="ma-0 mr-1" disabled>
                      <v-icon>chevron_left</v-icon>
                    </v-btn>
                    <v-btn small icon class="ma-0 mr-1" disabled>
                      <v-icon>chevron_right</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-flex>
            </v-layout>
          </template>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-menu :disabled="disabled" offset-y>
          <v-tooltip bottom :open-delay="500" :close-delay="0" slot="activator">
            <v-btn depressed class="dropdown-btn mr-0" slot="activator">
              <span class="success--text subheading text-capitalize">
                Show
                <span class="text-lowercase">
                  {{ selectedRange }}
                </span>
              </span>
              <v-icon>arrow_drop_down</v-icon>
            </v-btn>
            <span>Date range shortcuts</span>
          </v-tooltip>
          <v-list dense>
            <template v-for="(item, index) in shortcuts">
              <v-divider v-if="item.divider" :key="index" />
              <v-list-tile v-else :key="index" @click="item.action">
                <v-list-tile-title>{{ item.title }}</v-list-tile-title>
              </v-list-tile>
            </template>
          </v-list>
        </v-menu>

        <v-tooltip v-if="dates.start && dates.end" bottom :open-delay="500" :close-delay="0">
          <v-btn :disabled="disabled" icon class="ml-2" depressed slot="activator" @click="onShortcut(shortcutTypes.all)">
            <v-icon>
              clear
            </v-icon>
          </v-btn>
          <span>Clear date range filter</span>
        </v-tooltip>
      </v-layout>
      <v-layout class="pt-3 pb-4">
        <v-flex class="text-xs-center caption">
          Available data from
          {{ prettyDate(availableDateRange.start) }} &ndash;
          {{ prettyDate(availableDateRange.end) }}
        </v-flex>
      </v-layout>
    </v-card-text>
  </v-card>
</template>

<script>
  import moment from 'moment'
  import {
    prettyDateFormat,
    backendDateTimeFormat,
  } from '@/components/mixins/MomentUtil'


  const shortcutTypes = {
    all: 0,
    pastTwoWeeks: 1,
    pastMonth: 2,
    pastThreeMonths: 3,
    pastYear: 4,
    today: 5,
  }

  const shortcutTitles = {
    all: 'All available data',
    today: 'Today',
    pastTwoWeeks: 'Past two weeks',
    pastMonth: 'Past month',
    pastThreeMonths: 'Past three months',
    pastYear: 'Past year',
  }

  export default {
    props: {
      availableDateRange: Object,
      index: Number,
      disabled: Boolean,
    },
    data() {
      return {
        showHelp: false,
        shortcutTypes,
        fab: false,
        customRangeDialog: false,
        dates: {
          start: null,
          end: null,
        },
        restrictions: {
          end: moment()
            .startOf('day')
            .toDate(),
        },
        modal: false,
      }
    },
    computed: {
      selectedRange() {
        const customRange = (start, end) =>
          `Custom range (${end.diff(start, 'days')} days)`
        let today = moment().startOf('day')

        if (!this.dates.start || !this.dates.end) {
          return shortcutTitles.all
        }

        let startMoment = moment(this.dates.start)
        let endMoment = moment(this.dates.end)


        if (this.availableDateRange && this.availableDateRange.start && this.availableDateRange.end &&
          startMoment.isSame(this.availableDateRange.start, 'days') &&
          endMoment.isSame(this.availableDateRange.end, 'days')) {
          return shortcutTitles.all
        }

        if (!endMoment.isSame(today, 'days')) {
          return customRange(startMoment, endMoment)
        }

        if (startMoment.isSame(endMoment, 'days')) {
          return shortcutTitles.today
        }

        if (startMoment.clone().add(14, 'days').isSame(endMoment, 'days')) {
          return shortcutTitles.pastTwoWeeks
        }
        if (startMoment.clone().add(1, 'months').isSame(endMoment, 'days')) {
          return shortcutTitles.pastMonth
        }

        if (startMoment.clone().add(3, 'months').isSame(endMoment, 'days')) {
          return shortcutTitles.pastThreeMonths
        }

        if (startMoment.clone().add(1, 'years').isSame(endMoment, 'days')) {
          return shortcutTitles.pastYear
        }

        return customRange(startMoment, endMoment)
      },
      shortcuts() {
        return [{
          title: shortcutTitles.all,
          action: () => this.onShortcut(shortcutTypes.all),
        },
        {
          title: shortcutTitles.today,
          action: () => this.onShortcut(shortcutTypes.today),
        },
        {
          title: shortcutTitles.pastTwoWeeks,
          action: () => this.onShortcut(shortcutTypes.pastTwoWeeks),
        },
        {
          title: shortcutTitles.pastMonth,
          action: () => this.onShortcut(shortcutTypes.pastMonth),
        },
        {
          title: shortcutTitles.pastThreeMonths,
          action: () => this.onShortcut(shortcutTypes.pastThreeMonths),
        },
        {
          title: shortcutTitles.pastYear,
          action: () => this.onShortcut(shortcutTypes.pastYear),
        },
        {
          divider: true,
        },
        ]
      },
      activeFab() {
        switch (this.tabs) {
        case 'one': {
          return {
            class: 'purple',
            icon: 'account_circle',
          }
        }
        case 'two': {
          return {
            class: 'red',
            icon: 'edit',
          }
        }
        case 'three': {
          return {
            class: 'green',
            icon: 'keyboard_arrow_up',
          }
        }
        default: {
          return {}
        }
        }
      },
      prettyStartDate: {
        get() {
          let start = this.dates.start || (this.availableDateRange && this.availableDateRange.start)
          if (!start) return ''
          return moment(start).format(prettyDateFormat)
        },
        set() {},
      },
      prettyEndDate: {
        get() {
          let end = this.dates.end || (this.availableDateRange && this.availableDateRange.end)
          if (!end) return ''
          return moment(end).format(prettyDateFormat)
        },
        set() {},
      },
      hasNextStartDate() {
        let startMoment = moment(this.dates.start)
        let endRestriction = moment(this.restrictions.end)
        return startMoment.isBefore(endRestriction, 'days')
      },
      hasNextEndDate() {
        let endMoment = moment(this.dates.end)
        let endRestriction = moment(this.restrictions.end)
        return endMoment.isBefore(endRestriction, 'days')
      },
    },
    watch: {
      'dates.start': {
        immediate: true,
        handler(value) {
          let startMoment = moment(value)
          let endMoment = moment(this.dates.end)
          if (startMoment.isAfter(endMoment, 'days'))
            this.dates.end = startMoment.clone().toDate()

          this.emitUpdate(this.emitDateFormat())
        },
      },
      'dates.end': {
        immediate: true,
        handler(value) {
          let endMoment = moment(value)
          let startMoment = moment(this.dates.start)
          if (endMoment.isBefore(startMoment, 'days'))
            this.dates.start = endMoment.clone().toDate()

          this.emitUpdate(this.emitDateFormat())
        },
      },
    },
    methods: {
      emitUpdate(value) {
        this.$emit('dateRange', value)
      },
      prettyDate(date) {
        if (!date) return
        return moment(date).format(prettyDateFormat)
      },
      emitDateFormat() {
        let start = this.dates.start
        let end = this.dates.end

        if (!start || !end) return {}
        return {
          start: moment(start).format(backendDateTimeFormat),
          end: moment(end).format(backendDateTimeFormat),
        }
      },
      onShortcut(action) {
        let endMoment = moment()
        let startMoment = endMoment.clone()

        if (action == shortcutTypes.all) {
          startMoment = null
          endMoment = null
        } else if (action == shortcutTypes.today) {
          // Do nothing
        } else if (action == shortcutTypes.pastTwoWeeks) {
          startMoment.add(-14, 'days')
        } else if (action == shortcutTypes.pastMonth) {
          startMoment.add(-1, 'months')
        } else if (action == shortcutTypes.pastThreeMonths) {
          startMoment.add(-3, 'months')
        } else if (action == shortcutTypes.pastYear) {
          startMoment.add(-1, 'years')
        }

        if (startMoment && endMoment) {
          this.$set(this.dates, 'start', startMoment.toDate())
          this.$set(this.dates, 'end', endMoment.toDate())
        } else {
          this.$set(this.dates, 'start', null)
          this.$set(this.dates, 'end', null)
        }
      },
      nextStartDate() {
        this.dates.start = moment(this.dates.start)
          .add(1, 'days')
          .toDate()
      },
      previousStartDate() {
        this.dates.start = moment(this.dates.start)
          .add(-1, 'days')
          .toDate()
      },
      nextEndDate() {
        this.dates.end = moment(this.dates.end)
          .add(1, 'days')
          .toDate()
      },
      previousEndDate() {
        this.dates.end = moment(this.dates.end)
          .add(-1, 'days')
          .toDate()
      },
      selectDateRange(dateRange, selectionOptions) {
        if (selectionOptions && selectionOptions.relativeDate) {
          this.dates = this.makeRelativeDates(dateRange, selectionOptions.relativeDate)
        } else {
          this.dates = dateRange
        }
      },
      makeRelativeDates(dateRange, relativeDate) {
        let start = null
        let end = null
        if (!dateRange) return {
          start,
          end,
        }

        const today = moment()
        let daysDifference = moment(relativeDate).diff(today, 'days')
        if (dateRange.start) {
          start = moment(dateRange.start).subtract(daysDifference, 'days')
        }

        if (dateRange.end) {
          end = moment(dateRange.end).subtract(daysDifference, 'days')
        }

        return {
          start,
          end,
        }
      },
    },
  }
</script>

<style scoped>
  .date-action:hover {
    color: #333;
  }
</style>
<style>
  .dropdown-btn>.v-btn__content {
    font-weight: normal !important;
    text-transform: initial !important;
  }

  /* ie 11 only */
  @media screen and (-ms-high-contrast: active),
  screen and (-ms-high-contrast: none) {
    .vc-container .vc-grid-container {
      width: 100%;
      display: -ms-grid;
    }

    .vc-container .vc-grid-container.vc-weeks {
      padding: 5px 6px 7px 6px;
    }

    .vc-container .vc-grid-container .vc-day-content {
      min-height: 1.8rem;
      width: 1.8rem;
      height: 1.8rem;
      margin: 0.1rem auto;
    }

    .vc-highlight {
      height: 100%;
      width: 100%;
    }

    .vc-rounded-full {
      border-radius: 0px;
    }

    .vc-day-content:hover {
      background-color: #eee !important;
      color: #2a4365 !important;
    }

    .vc-opacity-0 {
      visibility: hidden;
    }

    .vc-container .vc-grid-container .vc-day-content {
      width: 22px !important;
      height: 22px !important;
      min-height: 0px;
    }
  }
</style>